/**
 * IssueRemediationSection — Expandable wrapper for remediation guidance.
 *
 * Handles expand/collapse state and lazy-loads guidance only when needed.
 * Uses the useRemediationGuidance hook with conditional enabling.
 */

import { useState } from "react";
import { useRemediationGuidance } from "../hooks/use-remediation.js";
import { RemediationPanel } from "./remediation/remediation-panel.js";

interface IssueRemediationSectionProps {
  issueKey: string;
  className?: string;
}

export function IssueRemediationSection({
  issueKey,
  className = "",
}: IssueRemediationSectionProps) {
  const [expanded, setExpanded] = useState(false);

  // Only fetch when expanded
  const { data, isPending, isError } = useRemediationGuidance(
    issueKey,
    expanded,
  );

  if (!expanded) {
    return (
      <button
        className={`remediation-toggle ${className}`}
        onClick={() => setExpanded(true)}
        aria-expanded={false}
      >
        View remediation guidance
      </button>
    );
  }

  if (isPending) {
    return (
      <div className={`issue-remediation ${className}`}>
        <button
          className="remediation-toggle remediation-toggle--expanded"
          onClick={() => setExpanded(false)}
          aria-expanded={true}
        >
          ✕ Hide remediation guidance
        </button>
        <div className="loading-text">Loading guidance…</div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={`issue-remediation ${className}`}>
        <button
          className="remediation-toggle remediation-toggle--expanded"
          onClick={() => setExpanded(false)}
          aria-expanded={true}
        >
          ✕ Hide remediation guidance
        </button>
        <div className="text-danger">Could not load remediation guidance</div>
      </div>
    );
  }

  return (
    <div className={`issue-remediation ${className}`}>
      <button
        className="remediation-toggle remediation-toggle--expanded"
        onClick={() => setExpanded(false)}
        aria-expanded={true}
      >
        ✕ Hide remediation guidance
      </button>
      <RemediationPanel guidance={data.guidance} issueKey={issueKey} />
    </div>
  );
}
